import api, { ApiResponse } from '../core/api';

export type ReportColumnRole = 'reference' | 'customer' | 'partner';

export interface ReportColumn {
  key: string;
  label: string;
  field: string;
  type?: string;
  width?: number;
  role?: ReportColumnRole;
}

export interface ReportIdentityMeta {
  reference: { label: string } | null;
  customer: { label: string } | null;
  partner: { label: string } | null;
}

export interface ReportPersonInfo {
  name: string;
  phone: string;
  email: string;
}

export interface ReportDefinition {
  _id: string;
  name: string;
  slug: string;
  module: string;
  query: Record<string, unknown>;
  columns: ReportColumn[];
  filters: Array<Record<string, unknown>>;
  isSystem?: boolean;
}

export interface ReportRunResult {
  report: { name: string; slug: string; module: string };
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  meta: {
    total: number;
    returned?: number;
    limit?: number;
    dataSource: string;
    type: string;
    filterOptions?: Record<string, string[]>;
    identity?: ReportIdentityMeta;
  };
}

export interface DataSourceSchema {
  key: string;
  label: string;
  module: string;
  fields: Array<{ key: string; label: string; type?: string }>;
  filterOptions: Record<string, string[]>;
  identity?: ReportIdentityMeta;
  supportsAggregate: boolean;
}

export async function getReportSchema() {
  const res = await api.get('/reports/schema');
  return res.data.data as DataSourceSchema[];
}

export async function getReportDefinitions() {
  const res = await api.get('/reports/definitions');
  return res.data.data as ReportDefinition[];
}

export async function createReportDefinition(data: Record<string, unknown>) {
  const res = await api.post('/reports/definitions', data);
  return res.data.data as ReportDefinition;
}

export async function updateReportDefinition(id: string, data: Record<string, unknown>) {
  const res = await api.put(`/reports/definitions/${id}`, data);
  return res.data.data as ReportDefinition;
}

export async function deleteReportDefinition(id: string) {
  await api.delete(`/reports/definitions/${id}`);
}

export async function previewReport(data: Record<string, unknown>) {
  const res = await api.post('/reports/preview', data);
  return res.data.data as ReportRunResult;
}

export async function runReport(slug: string, params?: Record<string, unknown>) {
  const res = await api.get<ApiResponse<ReportRunResult>>(`/reports/${slug}/run`, { params });
  return res.data.data;
}

export async function getTripRequestReport(params?: Record<string, unknown>) {
  const res = await api.get<ApiResponse<ReportRunResult>>('/reports/operational/trip-requests', { params });
  return res.data.data;
}

export async function getPartnerWiseReport(params?: Record<string, unknown>) {
  const res = await api.get<ApiResponse<ReportRunResult>>('/reports/operational/partner-wise', { params });
  return res.data.data;
}
